import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunRecord,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';

export type PrivateWorkspaceAgentRunState = {
  activeRunId: string | null;
  runsById: Record<string, WorkspaceAgentRunRecord>;
};

export const EMPTY_PRIVATE_WORKSPACE_AGENT_RUN_STATE: PrivateWorkspaceAgentRunState = {
  activeRunId: null,
  runsById: {},
};

export const resetPrivateWorkspaceAgentRunState = (): PrivateWorkspaceAgentRunState =>
  EMPTY_PRIVATE_WORKSPACE_AGENT_RUN_STATE;

export const applyRunStarted = (
  state: PrivateWorkspaceAgentRunState,
  { createdAt, runId, sessionId }: WorkspaceAgentRunStartedEvent,
): PrivateWorkspaceAgentRunState => ({
  activeRunId: runId,
  runsById: {
    ...state.runsById,
    [runId]: {
      runId,
      sessionId,
      triggeringUserMessageId: null,
      startedAt: createdAt,
      status: 'active',
    },
  },
});

export const applyRunFinished = (
  state: PrivateWorkspaceAgentRunState,
  { createdAt, outcome, runId }: WorkspaceAgentRunFinishedEvent,
): PrivateWorkspaceAgentRunState => {
  const existing = state.runsById[runId];

  if (!existing) {
    return state;
  }

  return {
    activeRunId: state.activeRunId === runId ? null : state.activeRunId,
    runsById: {
      ...state.runsById,
      [runId]: {
        ...existing,
        finishedAt: createdAt,
        outcome,
        status: 'finished',
      },
    },
  };
};

/** Optimistic unlock when cancel HTTP succeeds before WSS `Run.finished` lands. */
export const applyCancelActiveRun = (state: PrivateWorkspaceAgentRunState): PrivateWorkspaceAgentRunState => {
  if (!state.activeRunId) {
    return state;
  }

  const activeRun = state.runsById[state.activeRunId];

  if (!activeRun || activeRun.status === 'finished') {
    return { ...state, activeRunId: null };
  }

  return {
    activeRunId: null,
    runsById: {
      ...state.runsById,
      [activeRun.runId]: {
        ...activeRun,
        status: 'cancelling',
      },
    },
  };
};

export type LinkActiveRunToUserMessageOptions = {
  /** Replace a stale anchor set before the triggering user message was confirmed. */
  allowRelink?: boolean;
};

export const applyLinkActiveRunToUserMessage = (
  state: PrivateWorkspaceAgentRunState,
  userMessageId: string,
  { allowRelink = false }: LinkActiveRunToUserMessageOptions = {},
): PrivateWorkspaceAgentRunState => {
  if (!state.activeRunId) {
    return state;
  }

  const activeRun = state.runsById[state.activeRunId];

  if (!activeRun) {
    return state;
  }

  if (activeRun.triggeringUserMessageId === userMessageId) {
    return state;
  }

  if (activeRun.triggeringUserMessageId !== null && !allowRelink) {
    return state;
  }

  return {
    ...state,
    runsById: {
      ...state.runsById,
      [activeRun.runId]: {
        ...activeRun,
        triggeringUserMessageId: userMessageId,
      },
    },
  };
};
